import { DataStore } from 'aws-amplify/datastore';
import { Bulletin, BulletinStatus, User, UserSettings } from '@alucio/aws-beacon-amplify/src/models';
import { BulletinInput, canManageBulletins, validateBulletinInput } from './bulletin.utils';

type BulletinAuthor = Pick<User, 'id' | 'tenantId' | 'role' | 'additionalRoles'>;

function requireLead(user: BulletinAuthor): void {
  if (!canManageBulletins(user)) throw new Error('Only LeadViewers can manage posts.');
}

async function getEditableBulletin(id: string, user: BulletinAuthor): Promise<Bulletin> {
  requireLead(user);
  const original = await DataStore.query(Bulletin, id);
  if (
    !original ||
    original.tenantId !== user.tenantId ||
    (original.status !== BulletinStatus.ACTIVE && original.status !== BulletinStatus.DRAFT)
  ) {
    throw new Error('This post is no longer available.');
  }
  return original;
}

export async function saveBulletin(
  input: BulletinInput,
  user: BulletinAuthor,
  id?: string,
  status: BulletinStatus.ACTIVE | BulletinStatus.DRAFT = BulletinStatus.ACTIVE,
): Promise<Bulletin> {
  requireLead(user);
  const values = validateBulletinInput(input, status !== BulletinStatus.DRAFT);
  const now = new Date().toISOString();
  if (id) {
    const original = await getEditableBulletin(id, user);
    if (original.status === BulletinStatus.ACTIVE && status === BulletinStatus.DRAFT) {
      throw new Error('A published post cannot be saved as a draft.');
    }
    return DataStore.save(
      Bulletin.copyOf(original, (draft) => {
        draft.title = values.title;
        draft.content = values.content;
        draft.status = status;
        if (original.status === BulletinStatus.DRAFT && status === BulletinStatus.ACTIVE) {
          draft.publishedAt = now;
        }
        draft.updatedAt = now;
        draft.updatedBy = user.id;
      }),
      status === BulletinStatus.DRAFT ? (post) => post.status.eq(BulletinStatus.DRAFT) : undefined,
    );
  }
  return DataStore.save(
    new Bulletin({
      ...values,
      tenantId: user.tenantId,
      status,
      publishedAt: status === BulletinStatus.ACTIVE ? now : undefined,
      createdAt: now,
      createdBy: user.id,
      updatedAt: now,
      updatedBy: user.id,
    }),
  );
}

export async function withdrawBulletin(id: string, user: BulletinAuthor): Promise<Bulletin> {
  const original = await getEditableBulletin(id, user);
  return DataStore.save(
    Bulletin.copyOf(original, (draft) => {
      draft.status = BulletinStatus.DELETED;
      draft.updatedAt = new Date().toISOString();
      draft.updatedBy = user.id;
    }),
  );
}

export async function markBulletinsViewed(
  tenantId: string,
  userId: string,
  bulletinIds: readonly string[],
): Promise<UserSettings> {
  const records = await DataStore.query(UserSettings, (settings) => settings.userId.eq(userId));
  const original = records.find((settings) => settings.userId === userId && settings.tenantId === tenantId);
  if (!original) throw new Error('User settings are not available.');

  return DataStore.save(UserSettings.copyOf(original, (draft) => {
    draft.viewedBulletinIds = Array.from(new Set([
      ...(original.viewedBulletinIds || []), ...bulletinIds,
    ]));
    draft.updatedAt = new Date().toISOString();
    draft.updatedBy = userId;
  }));
}
